import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const { FiTrendingUp, FiUsers, FiDollarSign, FiTicket, FiCalendar, FiArrowUpRight, FiMoreVertical } = FiIcons;

const StatCard = ({ title, value, change, icon: Icon, isPositive }) => (
  <div className="glass-card p-6 rounded-2xl inner-glow">
    <div className="flex justify-between items-start mb-4">
      <div className="w-12 h-12 rounded-xl bg-white/50 backdrop-blur-sm border border-white flex items-center justify-center text-indigo-600 shadow-sm">
        <SafeIcon icon={Icon} className="w-6 h-6" />
      </div>
      <div className={`flex items-center gap-1 text-xs font-bold ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
        {isPositive ? '+' : '-'}{change}%
        <SafeIcon icon={FiArrowUpRight} className={`w-3 h-3 ${!isPositive && 'rotate-90'}`} />
      </div>
    </div>
    <h3 className="text-slate-500 text-sm font-medium mb-1">{title}</h3>
    <div className="text-2xl font-bold text-slate-900">{value}</div>
  </div>
);

const AnalyticsDashboard = () => {
  const { t } = useLanguage();
  const [timeRange, setTimeRange] = useState('Last 30 Days');

  const revenueOption = {
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(30, 41, 59, 0.9)', textStyle: { color: '#fff' } },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true, top: '10%' },
    xAxis: { type: 'category', boundaryGap: false, data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], axisLine: { lineStyle: { color: '#e2e8b0' } }, axisLabel: { color: '#64748b' } },
    yAxis: { type: 'value', axisLine: { show: false }, splitLine: { lineStyle: { type: 'dashed', color: '#f1f5f9' } }, axisLabel: { color: '#64748b' } },
    series: [{
      name: 'Revenue', type: 'line', smooth: true, data: [12000, 19000, 15000, 22000, 18000, 26000, 24000], itemStyle: { color: '#6366f1' },
      areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(99, 102, 241, 0.3)' }, { offset: 1, color: 'rgba(99, 102, 241, 0)' }] } },
      lineStyle: { width: 4 }
    }]
  };

  const ranges = [
    { label: t('analyticsSection.ranges.d7'), value: 'Last 7 Days' },
    { label: t('analyticsSection.ranges.d30'), value: 'Last 30 Days' },
    { label: t('analyticsSection.ranges.all'), value: 'All Time' }
  ];

  return (
    <section id="analytics" className="py-24 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-50/20 blur-[120px] -z-10" />
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/50 backdrop-blur-md border border-white/50 text-indigo-700 text-xs font-semibold uppercase tracking-wider mb-4 shadow-sm">
              <SafeIcon icon={FiTrendingUp} className="w-4 h-4" /> {t('analyticsSection.badge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              {t('analyticsSection.title')} <span className="text-gradient">{t('analyticsSection.titleSpan')}</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 bg-white/50 backdrop-blur-md p-1.5 rounded-xl border border-white/60 shadow-sm">
            {ranges.map((range) => (
              <button key={range.value} onClick={() => setTimeRange(range.value)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${timeRange === range.value ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' : 'text-slate-500 hover:text-indigo-600 hover:bg-white/50'}`}>
                {range.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard title={t('analyticsSection.statRevenue')} value="€84,290.00" change="12.5" icon={FiDollarSign} isPositive={true} />
          <StatCard title={t('analyticsSection.statSold')} value="2,840" change="8.2" icon={FiTicket} isPositive={true} />
          <StatCard title={t('analyticsSection.statActive')} value="1,420" change="2.4" icon={FiUsers} isPositive={false} />
          <StatCard title={t('analyticsSection.statConversion')} value="3.42%" change="5.1" icon={FiTrendingUp} isPositive={true} />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 glass-panel p-8 rounded-[32px] inner-glow">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{t('analyticsSection.growthTitle')}</h3>
                <p className="text-sm text-slate-500">{t('analyticsSection.growthDesc')}</p>
              </div>
              <button className="w-8 h-8 rounded-lg bg-white/50 flex items-center justify-center text-slate-400 hover:text-indigo-600 transition-colors">
                <SafeIcon icon={FiMoreVertical} className="w-5 h-5" />
              </button>
            </div>
            <div className="h-[350px]">
              <ReactECharts option={revenueOption} style={{ height: '100%', width: '100%' }} />
            </div>
          </div>
          <div className="glass-panel p-8 rounded-[32px] inner-glow">
            <h3 className="text-lg font-bold text-slate-900 mb-8">{t('analyticsSection.quickActions')}</h3>
            <div className="space-y-4">
              {[
                { label: t('analyticsSection.actions.export'), value: 'export' },
                { label: t('analyticsSection.actions.sales'), value: 'sales' },
                { label: t('analyticsSection.actions.payout'), value: 'payout' }
              ].map((action, i) => (
                <button key={i} className="w-full flex items-center justify-between p-4 rounded-xl bg-white/50 hover:bg-white/80 border border-white/60 transition-all text-sm font-semibold text-slate-700">
                  {action.label}
                  <SafeIcon icon={FiArrowUpRight} className="w-4 h-4 text-indigo-500" />
                </button>
              ))}
            </div>
            <div className="mt-8 p-4 rounded-2xl bg-indigo-600/5 border border-indigo-100 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white">
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-900">{t('analyticsSection.nextPayout')}</div>
                <div className="text-sm text-indigo-600 font-bold">Oct 14, 2024</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnalyticsDashboard;